/* eslint-disable @typescript-eslint/no-unused-vars */
/* import type { ChangeEvent } from 'react'; */
import { forwardRef, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useFloating, flip, shift, offset } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
/* import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js'; */
/* import { useQueryDownloader } from '@/containers/QueryDownloader'; */
/* import { UploadFilesContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js'; */
/* import { RateLimitingContext } from '@/containers/RateLimiting/Context'; */
import { AITooltip } from '@trinity/chat-components/components/Chat/KolSearch.Profile.AITooltip.js';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
/* import { useCreateFolderInsideParentModal } from '@/components/Workspace.Folder/NewFolderModal'; */
import { useQueryDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { TagGeneratingFilesContext } from '@trinity/chat-components/containers/Workspace/context.js';
import { UploadFilesContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js';
import { useCanUpload } from '@trinity/chat-components/containers/WorkspaceFileUpload/hooks/index.js';
import { useCreateFolderInsideParentModal } from '@/components/Workspace.Folder/NewFolderModal';
import { WorkspaceObjectType } from '@/enums';
/* import { cx, getLocationFor } from '@trinity/chat-components'; */
/* import { useCanUpload } from '@/containers/WorkspaceFileUpload/hooks'; */
/* import { useDownloadAllModal } from './DownloadAll.Modal'; */
/* import { usePublishModal } from './PublishModal'; */
import { useApplyTagsModal } from './ApplyTags.Modal';
import { FilesTableContext, FilesTableMetaContext } from './context';
import { useSelectedObjects, useSelectObjectsEnabled } from './hooks';
import styles from './style/Files.Table.Actions.module.css';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat/hooks.js';

type Props = {
  className?: string;
};

const AnchorButton = memo(forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const { palette, theme: wl } = useContext(AppThemingContext);

  if (wl?.enabled) {
    return (
      <Button
        ref={ref}
        // color="transparent"
        className={props.className}
        style={{
          backgroundColor: palette.primary.main,
          color: palette.primary.text,
        }}
        variant="brick">
        Actions
      </Button>
    );
  }

  return (
    <Button
      ref={ref}
      color="primary"
      className={props.className}
      variant="brick">
      Actions
    </Button>
  );
}));

type ButtonProps = {
  className?: string;
};

type MenuItem = {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick: () => unknown;
};

export const FilesActionsButton = ({ className }: ButtonProps) => {

  const [open, setOpen] = useState(false);

  const apiClient = useChatApiClient();

  const selectObjectsEnabled = useSelectObjectsEnabled();
  const filesTableContext = useContext(FilesTableContext);
  const selectedObjects = useSelectedObjects();
  const onUpload = useContext(UploadFilesContext);
  const hasUploadPermissions = useCanUpload();
  const [toggleNewFolderModal, NewFolderModal] = useCreateFolderInsideParentModal();
  const [toggleTagsModal, ApplyTagsModal] = useApplyTagsModal();

  const canDownload = useMemo(() => !!filesTableContext.parentObject, [filesTableContext.parentObject]);
  const features = useGroupFeatures();

  const zIndexMiddleware = useZIndexMiddleware();

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      zIndexMiddleware,
    ],
  });

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  const handleUploadClick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files.length) {
      onUpload(Array.from(e.target.files));
    }
    setOpen(false);
  }, [
    onUpload,
  ]);

  const handleImportTranscripts = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files.length) {
      onUpload(Array.from(e.target.files), true);
    }
    setOpen(false);
  }, [
    onUpload,
  ]);

  const handleAddFolderClick = useCallback(() => {
    toggleNewFolderModal();
    setOpen(false);
  }, [toggleNewFolderModal]);

  const { download } = useQueryDownloader({
    queryKey: [
      `get:workspaces/objects`, {
        objectId: filesTableContext.parentObject?.id,
        workspaceId: filesTableContext.parentObject?.workspaceId,
      },
    ],
    queryFn: () => {
      return apiClient.workspacesObject.downloadObjectRecursive({
        objectId: filesTableContext.parentObject?.id,
        workspaceId: filesTableContext.parentObject?.workspaceId,
        objectIds: selectedObjects?.map(o => o.object.id) || [],
      });
    },
    downloaderOptions: {
      onResponse: res => ({
        type: 'websocket',
        value: res.websocketKey,
      }),
    },
  });

  const handleDownloadAll = useCallback(() => {
    download({
      extension: `zip`,
      name: `Workspace Files`,
      title: `Generating Archive`,
    });

    setOpen(false);
  }, [download]);

  const downloadAll: MenuItem = useMemo(() => {
    if (!canDownload) return null;

    const enabled = !selectedObjects?.length || selectedObjects.some(o => DownloadableObjectTypes.includes(o.object.typeId));

    return {
      children: selectedObjects?.length ? 'Download Selected' : 'Download All',
      disabled: !enabled,
      onClick: handleDownloadAll,
    };
  }, [
    canDownload,
    handleDownloadAll,
    selectedObjects,
  ]);

  const uploadFileItem: MenuItem = useMemo(() => {
    if (!hasUploadPermissions) return null;

    const disabled = selectedObjects.length > 0;

    const buttonText = `Upload File(s)`;
    return {
      className: styles.noPadding,
      disabled,
      children: (
        <label>
          <input
            className={styles.fileInput}
            multiple={true}
            disabled={disabled}
            onChange={handleUploadClick}
            type="file" />
          {buttonText}
        </label>
      ),
      onClick: () => { },
    };
  }, [handleUploadClick, hasUploadPermissions, selectedObjects.length]);

  const addFolderItem: MenuItem = useMemo(() => {
    if (!hasUploadPermissions) return null;

    const disabled = selectedObjects.length > 0;
    return {
      children: 'Add Folder',
      disabled,
      onClick: handleAddFolderClick,
    };
  }, [hasUploadPermissions, selectedObjects.length, handleAddFolderClick]);

  const handleAddTagsClick = useCallback(() => {
    toggleTagsModal();
    setOpen(false);
  }, [toggleTagsModal]);

  const massAddTags = useMemo<MenuItem>(() => {
    if (!selectObjectsEnabled) return null;

    const taggableObjectTypes = [WorkspaceObjectType.File, WorkspaceObjectType.Folder, WorkspaceObjectType.CallTranscript, WorkspaceObjectType.ProjectParent];
    const taggableObjects = selectedObjects.filter(o => taggableObjectTypes.includes(o.object.typeId));

    return {
      disabled: !taggableObjects.length,
      onClick: handleAddTagsClick,
      children: 'Apply Tags',
    };
  }, [selectObjectsEnabled, selectedObjects, handleAddTagsClick]);

  const tagGeneratingContext = useContext(TagGeneratingFilesContext);

  const handleGenerateTags = useCallback(async () => {
    const fileObjects = selectedObjects.filter(o => o.object.typeId === WorkspaceObjectType.File && 'file' in o && o.file !== null);
    const fileIds = fileObjects.map(o => ('file' in o && o.file ? o.file.id : 0)).filter(id => id > 0);

    if (fileIds.length === 0) {
      return;
    }

    try {
      console.log('[TagGeneration] Starting tag generation for files:', fileIds);

      if (tagGeneratingContext) {
        // Track initial lastModifiedOn timestamps for each file
        const initialTimestamps = new Map<number, Date>();
        fileObjects.forEach(obj => {
          if ('file' in obj && obj.file) {
            initialTimestamps.set(obj.file.id, obj.file.lastModifiedOn);
          }
        });

        console.log('[TagGeneration] Initial timestamps:', Array.from(initialTimestamps.entries()));
        tagGeneratingContext.addGeneratingFiles(fileIds, initialTimestamps);
        console.log('[TagGeneration] Added files to generating state');
      }

      const result = await apiClient.workspaces.generateTags({ fileIds });
      setOpen(false);

      Toast.info({
        title: 'Tag generation started',
        body: `Tag generation queued for ${result.queued} ${result.queued === 1 ? 'file' : 'files'}. Tags will appear when processing completes.`,
      });
    } catch (error) {
      console.error('[TagGeneration] Error:', error);

      if (tagGeneratingContext) {
        fileIds.forEach(fileId => tagGeneratingContext.removeGeneratingFile(fileId));
      }

      Toast.error({
        title: 'Failed to generate tags',
        body: error instanceof Error ? error.message : 'An error occurred while starting tag generation.',
      });
    }
  }, [apiClient, selectedObjects, tagGeneratingContext]);

  const generateTags = useMemo<MenuItem>(() => {
    if (!selectObjectsEnabled || !features.autoTagFiles) return null;

    const fileObjects = selectedObjects.filter(o => o.object.typeId === WorkspaceObjectType.File && 'file' in o && o.file !== null);

    return {
      disabled: !fileObjects.length,
      onClick: handleGenerateTags,
      children: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>Generate Tags</span>
          <AITooltip helpText="This AI-powered feature automatically analyzes your documents and generates relevant tags based on their content. Tags help organize and categorize your files for easier discovery." />
        </div>
      ),
    };
  }, [selectObjectsEnabled, selectedObjects, features.autoTagFiles, handleGenerateTags]);

  const importTranscripts: MenuItem = useMemo(() => {
    if (!hasUploadPermissions) return null;

    const disabled = selectedObjects.length > 0;
    const acceptedFileTypes = ['pdf', 'doc', 'docx'].map(type => `.${type}`).join(',');
    const buttonText = 'Import Transcripts';

    return {
      className: styles.noPadding,
      disabled,
      children: (
        <label>
          <input
            className={styles.fileInput}
            accept={acceptedFileTypes}
            multiple={true}
            disabled={disabled}
            onChange={handleImportTranscripts}
            type="file" />
          {buttonText}
        </label>
      ),
      onClick: () => { },
    };
  }, [hasUploadPermissions, selectedObjects.length, handleImportTranscripts]);

  const items = [
    uploadFileItem,
    addFolderItem,
    downloadAll,
    importTranscripts,
    massAddTags,
    generateTags,
  ].filter(x => x !== null);

  return (
    <>
      <div
        className={className}
        ref={refs.setReference}
        onClick={handleClick}>
        <AnchorButton />
      </div>
      <NewFolderModal parentObject={filesTableContext.parentObject} />
      <ApplyTagsModal />
      {open &&
        <Portal>
          <ClickAwayListener
            mouseEvent='onMouseDown'
            onClickAway={handleBlur}
            touchEvent='onTouchStart'>
            <div
              ref={refs.setFloating}
              style={floatingStyles}>
              <PopperMenu>
                {items.map((m, i) => {
                  return (
                    <PopperMenuItem
                      key={i}
                      {...m}
                      className={styles.menuItem}
                      onClick={m.onClick} />
                  );
                })}
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      }
    </>
  );
};

const DownloadableObjectTypes = [WorkspaceObjectType.File, WorkspaceObjectType.CallTranscript, WorkspaceObjectType.Folder];